import type { MarketSurveyRow } from '../../../domain/demand/marketViews';
import type { PersonaRange } from './PersonaDistributionBar';

/**
 * The demand model only separates budget and premium travellers. The six display personas are an indicative split
 * of those two numbers with fixed weights, not simulation output:
 *   premium  -> Corporate 50%, Executive 25%, Luxury 25%
 *   middle   -> VFR 60%, Last-Minute 40%        (middle = 100 - budget - premium)
 *   budget   -> Budget 100%
 * Ranges reuse the survey's +/-15% fuzz.
 */
const FUZZ = 0.15;

const PERSONA_COLORS = {
  Budget: '#2d3139',
  Luxury: '#7c3aed',
  VFR: '#dc2626',
  Corporate: '#2563eb',
  Executive: '#0891b2',
  'Last-Minute': '#d97706',
} as const;

function range(label: keyof typeof PERSONA_COLORS, pct: number): PersonaRange {
  return {
    label,
    minPct: pct * (1 - FUZZ),
    maxPct: pct * (1 + FUZZ),
    estimatePct: pct,
    color: PERSONA_COLORS[label],
  };
}

export function personaMixOf(row: Pick<MarketSurveyRow, 'budgetPct' | 'premiumPct'>): readonly PersonaRange[] {
  const budget = row.budgetPct;
  const premium = row.premiumPct;
  const middle = Math.max(0, 100 - budget - premium);
  return [
    range('Budget', budget),
    range('Luxury', premium * 0.25),
    range('VFR', middle * 0.6),
    range('Corporate', premium * 0.5),
    range('Executive', premium * 0.25),
    range('Last-Minute', middle * 0.4),
  ];
}
